import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db, auth } from "../../services/firebase";
import { WeeklyPushNotification } from "../../types";
import { getApiBaseUrl } from "../../services/api";

// 一覧に出す週数。過去分は履歴として見えれば足りる
const DISPLAYED_WEEK_COUNT = 12;

// Firestoreのドキュメントをアプリ用の型に変換
const convertWeeklyPushNotification = (
  weekId: string,
  data: Record<string, unknown>
): WeeklyPushNotification => {
  const toIsoString = (value: unknown): string =>
    (value as Timestamp | null)?.toDate?.().toISOString() || "";

  return {
    weekId,
    title: data.title as string,
    body: data.body as string,
    topicId: (data.topicId as string) || "",
    scheduledAt: toIsoString(data.scheduledAt),
    sentAt: toIsoString(data.sentAt) || null,
    enabled: data.enabled as boolean,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
    createdBy: (data.createdBy as string) || "",
  };
};

/** ISO形式の日時をJSTの「8/3(月) 8:00」形式にする */
const formatJstDateTime = (isoString: string): string => {
  if (!isoString) {
    return "未設定";
  }
  return new Date(isoString).toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

const PushNotificationSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<WeeklyPushNotification | null>(
    null
  );

  // フォーム状態
  const [formTitle, setFormTitle] = useState("");
  const [formBody, setFormBody] = useState("");

  // 送信結果
  const [sendResult, setSendResult] = useState<{
    success: boolean;
    sent?: number;
    failed?: number;
  } | null>(null);

  // 週次通知の一覧取得
  const {
    data: notificationsData,
    isLoading,
    error,
  } = useQuery(
    "weeklyPushNotifications",
    async () => {
      const q = query(
        collection(db, "weeklyPushNotifications"),
        orderBy("weekId", "desc"),
        limit(DISPLAYED_WEEK_COUNT)
      );
      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map((docSnap) =>
        convertWeeklyPushNotification(docSnap.id, docSnap.data())
      );
      return { notifications };
    },
    {
      staleTime: 1000 * 60 * 5, // 5分
    }
  );

  // 文面の更新
  const updateMutation = useMutation(
    async (data: { weekId: string; title?: string; body?: string; enabled?: boolean }) => {
      const { weekId, ...updateFields } = data;
      await updateDoc(doc(db, "weeklyPushNotifications", weekId), {
        ...updateFields,
        updatedAt: serverTimestamp(),
      });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("weeklyPushNotifications");
        handleCloseEditDialog();
      },
    }
  );

  // 予定時刻を待たずに送信
  const sendMutation = useMutation(
    async (data: { weekId: string }) => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Not authenticated");
      }
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${getApiBaseUrl()}/admin/push-notifications/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to send push notifications");
      }
      return response.json();
    },
    {
      onSuccess: (result) => {
        setSendResult(result);
        queryClient.invalidateQueries("weeklyPushNotifications");
      },
    }
  );

  const handleOpenEditDialog = (notification: WeeklyPushNotification) => {
    setSelectedNotification(notification);
    setFormTitle(notification.title);
    setFormBody(notification.body);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedNotification(null);
  };

  const handleSave = () => {
    if (!selectedNotification || !formTitle.trim() || !formBody.trim()) return;

    updateMutation.mutate({
      weekId: selectedNotification.weekId,
      title: formTitle.trim(),
      body: formBody.trim(),
    });
  };

  const handleToggleEnabled = (notification: WeeklyPushNotification) => {
    updateMutation.mutate({
      weekId: notification.weekId,
      enabled: !notification.enabled,
    });
  };

  const handleOpenSendDialog = (notification: WeeklyPushNotification) => {
    setSelectedNotification(notification);
    setSendResult(null);
    setSendDialogOpen(true);
  };

  const handleSendNotification = () => {
    if (selectedNotification) {
      sendMutation.mutate({ weekId: selectedNotification.weekId });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">週次通知の取得に失敗しました</Alert>;
  }

  const notifications = notificationsData?.notifications || [];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        プッシュ通知
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        週に1通、その週の月曜8時に送る通知です。文面は週次パイプラインがその週のお題から作ります。
        ここでは内容の確認と手直し、送信の停止ができます。
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <NotificationsIcon color="primary" />
          <Typography variant="h6">週ごとの通知（直近{DISPLAYED_WEEK_COUNT}週）</Typography>
        </Box>

        {notifications.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
          >
            <NotificationsIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography color="text.secondary" align="center">
              登録されている通知がありません
              <br />
              週次パイプラインがお題を公開すると、その週の通知が作られます
            </Typography>
          </Box>
        ) : (
          <List>
            {notifications.map((notification) => (
              <ListItem
                key={notification.weekId}
                sx={{
                  bgcolor: notification.enabled ? "background.paper" : "grey.100",
                  borderRadius: 1,
                  mb: 1,
                  border: "1px solid",
                  borderColor: notification.enabled ? "divider" : "grey.300",
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{
                          color: notification.enabled ? "text.primary" : "text.secondary",
                        }}
                      >
                        {notification.weekId}
                      </Typography>
                      {!notification.enabled && <Chip label="無効" size="small" color="default" />}
                      {notification.sentAt ? (
                        <Chip
                          label={`送信済み ${formatJstDateTime(notification.sentAt)}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label={`送信予定 ${formatJstDateTime(notification.scheduledAt)}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{notification.title}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {notification.body}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={notification.enabled}
                    onChange={() => handleToggleEnabled(notification)}
                    size="small"
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenSendDialog(notification)}
                    sx={{ ml: 1 }}
                    title="今すぐ送信"
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditDialog(notification)}
                    sx={{ ml: 1 }}
                    title="文面を編集"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* 編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedNotification?.weekId} の通知を編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="通知タイトル"
            fullWidth
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            helperText={`${formTitle.length}文字（ロック画面では24文字前後で省略されます）`}
            sx={{ mt: 1 }}
          />

          <TextField
            margin="dense"
            label="通知本文"
            fullWidth
            multiline
            rows={3}
            value={formBody}
            onChange={(e) => setFormBody(e.target.value)}
            sx={{ mt: 2 }}
          />

          {selectedNotification?.sentAt && (
            <Alert severity="info" sx={{ mt: 2 }}>
              この週の通知は送信済みです。文面を直しても、届いた内容は変わりません。
            </Alert>
          )}

          {updateMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              保存に失敗しました。もう一度お試しください。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formTitle.trim() || !formBody.trim() || updateMutation.isLoading}
          >
            {updateMutation.isLoading ? <CircularProgress size={20} /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 送信確認ダイアログ */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
        <DialogTitle>プッシュ通知を送信</DialogTitle>
        <DialogContent>
          {sendResult ? (
            <Box>
              <Alert severity={sendResult.success ? "success" : "error"} sx={{ mb: 2 }}>
                {sendResult.success
                  ? `通知を送信しました（成功: ${sendResult.sent}件、失敗: ${sendResult.failed}件）`
                  : "通知の送信に失敗しました"}
              </Alert>
            </Box>
          ) : (
            <Box>
              <Typography>以下の内容で全ユーザーに今すぐ送信しますか？</Typography>
              <Paper sx={{ p: 2, mt: 2, bgcolor: "grey.50" }}>
                <Typography variant="subtitle2">{selectedNotification?.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedNotification?.body}
                </Typography>
              </Paper>
              {selectedNotification?.sentAt ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  この週の通知はすでに送信済みです。送ると同じ内容がもう一度届きます。
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  送信すると送信済みになるため、
                  {formatJstDateTime(selectedNotification?.scheduledAt || "")}
                  の予定送信は行われません。
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>閉じる</Button>
          {!sendResult && (
            <Button
              color="primary"
              variant="contained"
              onClick={handleSendNotification}
              disabled={sendMutation.isLoading}
              startIcon={sendMutation.isLoading ? <CircularProgress size={16} /> : <SendIcon />}
            >
              送信
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotificationSettings;
